import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface GoalDeposit {
  id: string;
  user_id: string;
  goal_id: string;
  account_id: string | null;
  type: 'deposit' | 'withdrawal';
  amount: number;
  note: string | null;
  date: string;
  created_at: string;
}

interface AddDepositParams {
  goal_id: string;
  account_id: string | null;
  type: 'deposit' | 'withdrawal';
  amount: number;
  note?: string;
  date?: string;
}

export function useGoalDeposits(goalId?: string) {
  const { user } = useAuth();
  const [deposits, setDeposits] = useState<GoalDeposit[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDeposits = useCallback(async () => {
    if (!user || !goalId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('goal_deposits')
        .select('*')
        .eq('goal_id', goalId)
        .order('date', { ascending: true });

      if (error) throw error;
      setDeposits((data as GoalDeposit[]) || []);
    } catch (err) {
      console.error('Error fetching goal deposits:', err);
    } finally {
      setLoading(false);
    }
  }, [user, goalId]);

  useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);

  const addDeposit = useCallback(
    async (params: AddDepositParams): Promise<boolean> => {
      if (!user) return false;
      try {
        const { error } = await supabase.from('goal_deposits').insert({
          user_id: user.id,
          goal_id: params.goal_id,
          account_id: params.account_id || null,
          type: params.type,
          amount: params.amount,
          note: params.note || null,
          date: params.date || new Date().toISOString().split('T')[0],
        });
        if (error) throw error;
        await fetchDeposits();
        window.dispatchEvent(new CustomEvent('achievements-need-refresh'));
        return true;
      } catch (err) {
        console.error('Error adding goal deposit:', err);
        return false;
      }
    },
    [user, fetchDeposits]
  );

  const deleteDeposit = useCallback(
    async (depositId: string): Promise<boolean> => {
      if (!user) return false;
      try {
        const { error } = await supabase.from('goal_deposits').delete().eq('id', depositId);
        if (error) throw error;
        await fetchDeposits();
        return true;
      } catch (err) {
        console.error('Error deleting goal deposit:', err);
        return false;
      }
    },
    [user, fetchDeposits]
  );

  const deleteAllForGoal = useCallback(
    async (gId: string): Promise<boolean> => {
      if (!user) return false;
      try {
        const { error } = await supabase.from('goal_deposits').delete().eq('goal_id', gId);
        if (error) throw error;
        return true;
      } catch (err) {
        console.error('Error deleting goal deposits:', err);
        return false;
      }
    },
    [user]
  );

  // Computed values
  const totalDeposited = deposits
    .filter((d) => d.type === 'deposit')
    .reduce((sum, d) => sum + Number(d.amount), 0);

  const totalWithdrawn = deposits
    .filter((d) => d.type === 'withdrawal')
    .reduce((sum, d) => sum + Number(d.amount), 0);

  const currentAmount = totalDeposited - totalWithdrawn;

  // Cumulative data for chart (sorted by date)
  const cumulativeData = deposits.reduce<{ date: string; amount: number }[]>((acc, d) => {
    const prev = acc.length > 0 ? acc[acc.length - 1].amount : 0;
    const delta = d.type === 'deposit' ? Number(d.amount) : -Number(d.amount);
    acc.push({ date: d.date, amount: prev + delta });
    return acc;
  }, []);

  return {
    deposits,
    loading,
    addDeposit,
    deleteDeposit,
    deleteAllForGoal,
    totalDeposited,
    totalWithdrawn,
    currentAmount,
    cumulativeData,
    refetch: fetchDeposits,
  };
}
